/**
 * Fills in rows that came back `pending` (TZ §12).
 *
 * A grid page arrives already enriched, so this runs only for the anomaly: a
 * string the ingest hooks missed, or one older than the backfill window. It is
 * polling, not sockets — a handful of requests per page at most.
 *
 * Rules from §12, all of them load-bearing:
 *   - first retry after 2s, the interval doubling each time: 2 → 4 → 8 → 16;
 *   - four attempts, then stop. Whatever is still pending stays an empty cell
 *     and comes from the cache on the next visit;
 *   - leaving the page stops it.
 *
 * `pending` MUST be derived from the server response, never from rows this hook
 * has already patched: the input list is what the effect keys on, so feeding it
 * back would restart the schedule every time an answer landed and turn four
 * attempts into an unbounded loop.
 */

import { useEffect, useState } from "react";

import { api } from "@/lib/api";

export type TranslationPatch = { status: string; text: string | null };

const FIRST_DELAY_MS = 2_000;
const MAX_ATTEMPTS = 4;
const MAX_TEXTS = 200;   // §8 — the service's own per-call ceiling.

export function useTranslationPolling(
  pending: string[],
): Map<string, TranslationPatch> {
  const [patches, setPatches] = useState<Map<string, TranslationPatch>>(
    () => new Map(),
  );
  // The identity of the request, not of the array: React hands us a fresh array
  // on every render, and keying the effect on it would re-arm the timer forever.
  const key = pending.join(" ");

  useEffect(() => {
    if (!pending.length) return;

    let alive = true;
    let timer: ReturnType<typeof setTimeout> | undefined;
    let remaining = pending.slice(0, MAX_TEXTS);
    let attempt = 0;
    let delay = FIRST_DELAY_MS;

    const tick = async () => {
      attempt += 1;
      try {
        const { data } = await api.post<{
          results: { source_text: string; status: string; text: string | null }[];
        }>("/api/v1/translations/lookup", { texts: remaining });
        if (!alive) return;

        const settled = new Map<string, TranslationPatch>();
        for (const r of data.results ?? []) {
          if (r.status === "pending") continue;
          settled.set(r.source_text, { status: r.status, text: r.text });
        }
        if (settled.size) {
          setPatches((prev) => new Map([...prev, ...settled]));
          remaining = remaining.filter((t) => !settled.has(t));
        }
      } catch {
        // §12 — a translation is never an error the user sees. A failed poll is
        // simply an attempt spent; the cell stays empty and the next visit
        // reads it from the cache.
      }
      if (!alive || !remaining.length || attempt >= MAX_ATTEMPTS) return;
      delay *= 2;
      timer = setTimeout(() => void tick(), delay);
    };

    timer = setTimeout(() => void tick(), delay);
    return () => {
      alive = false;
      if (timer) clearTimeout(timer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);

  return patches;
}

/** Overlay whatever the poll has resolved onto a page of rows. */
export function applyTranslationPatches<
  T extends { translation_en?: string | null; translation_status?: string | null },
>(rows: T[], patches: Map<string, TranslationPatch>, textOf: (row: T) => string | null | undefined): T[] {
  if (!patches.size) return rows;
  return rows.map((r) => {
    const p = patches.get(textOf(r) ?? "");
    return p ? { ...r, translation_status: p.status, translation_en: p.text } : r;
  });
}
